import { AnalyticsReportRequester, PerformanceReports, type ReportFormat, type ReportType } from '@cobliteam/api-lib';
import type { DateRange } from '@cobliteam/api-lib/src/__generated__/graphql';
import type { SpeedEventType, UUID } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import Alert from '@cobliteam/ui/components/Alert';
import { useMutation } from '@tanstack/react-query';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getAnalyticsReportRequester = () =>
  new AnalyticsReportRequester({
    host: env.apiHost,
  });
const getPerformanceReportsRequester = () =>
  new PerformanceReports({
    host: env.herbieApiHost,
  });

interface ReportVariables {
  startTimestamp: number;
  endTimestamp: number;
  destinationEmails: string[];
}

interface IdleEngineReportVariables extends ReportVariables {
  idleEngineThresholdInMinutes?: number;
  reportType: ReportType;
  wantedIds?: UUID[];
}

interface IdleEngineEventsReportVariables {
  startTimestamp: number;
  endTimestamp: number;
  wantedId: UUID;
  destinationEmails: string[];
  idleEngineThresholdInMinutes?: number;
}

interface GeofenceEventsReportVariables {
  startTimestamp: number;
  endTimestamp: number;
  destinationEmails: string[];
  vehicleIds?: UUID[];
  groupIds?: UUID[];
  geofenceIds?: UUID[];
  geofenceTypes?: string[];
  minDurationInSeconds?: number;
  hourBegin?: number;
  hourEnd?: number;
}

interface DriverVehicleAssociationVariables {
  startDate: number;
  endDate: number;
  destinationEmails: string[];
  wantedIds?: UUID[];
}

interface DriverSafetyReportVariables extends ReportVariables {
  driverIds: UUID[];
  reportFormat: ReportFormat;
  speedEventType: SpeedEventType;
}

interface ProductivityReportVariables extends ReportVariables {
  reportType: ReportType;
  wantedIds: UUID[];
}

interface SpeedingPathReportVariables {
  startTimestamp: number;
  endTimestamp: number;
  vehicleId: string;
  reportFormat: string;
}

interface TriggerEventsReportVariables extends ReportVariables {
  ruleId: UUID;
  ruleName?: string;
  vehicleGroupIds?: UUID[];
  vehicleIds?: UUID[];
  geofenceIds?: UUID[];
  conditionIds?: UUID[];
}

const useAnalyticsReport = () => {
  const publishVehicleIdleEngineReportRequest = useMutation<string, unknown, IdleEngineReportVariables>({
    mutationFn: (variables) =>
      getAnalyticsReportRequester().requestVehicleIdleEngineReportToEmail(
        variables.startTimestamp,
        variables.endTimestamp,
        variables.reportType,
        variables.destinationEmails,
        variables.wantedIds,
        variables.idleEngineThresholdInMinutes
      ),
  });

  const publishVehicleIdleEngineEventReportRequest = useMutation<string, unknown, IdleEngineEventsReportVariables>({
    mutationFn: (variables) =>
      getAnalyticsReportRequester().requestVehicleIdleEngineEventsReportToEmail(
        variables.startTimestamp,
        variables.endTimestamp,
        variables.wantedId,
        variables.destinationEmails,
        variables.idleEngineThresholdInMinutes
      ),
  });

  const publishDriverIdleEngineReportRequest = useMutation<string, unknown, IdleEngineReportVariables>({
    mutationFn: (variables) =>
      getAnalyticsReportRequester().requestDriverIdleEngineReportToEmail(
        variables.startTimestamp,
        variables.endTimestamp,
        variables.reportType,
        variables.destinationEmails,
        variables.wantedIds,
        variables.idleEngineThresholdInMinutes
      ),
  });

  const publishDriverIdleEngineEventReportRequest = useMutation<string, unknown, IdleEngineEventsReportVariables>({
    mutationFn: (variables) =>
      getAnalyticsReportRequester().requestDriverIdleEngineEventsReportToEmail(
        variables.startTimestamp,
        variables.endTimestamp,
        variables.wantedId,
        variables.destinationEmails,
        variables.idleEngineThresholdInMinutes
      ),
  });

  const publishGeofenceEventsReportRequest = useMutation<string, unknown, GeofenceEventsReportVariables>({
    mutationFn: (variables) =>
      getAnalyticsReportRequester().requestGeofenceEventsReportToEmail(
        variables.startTimestamp,
        variables.endTimestamp,
        variables.destinationEmails,
        variables.vehicleIds,
        variables.groupIds,
        variables.geofenceIds,
        variables.geofenceTypes,
        variables.minDurationInSeconds,
        variables.hourBegin,
        variables.hourEnd
      ),
  });

  const publishDriverVehicleAssociationReportByVehicleRequest = useMutation<string, unknown, DriverVehicleAssociationVariables>({
    mutationFn: (variables) =>
      getAnalyticsReportRequester().requestDriverVehicleAssociationReportByVehicleToEmail(variables.startDate, variables.endDate, variables.destinationEmails, variables.wantedIds),
  });

  const publishDriverVehicleAssociationReportByDriverRequest = useMutation<string, unknown, DriverVehicleAssociationVariables>({
    mutationFn: (variables) =>
      getAnalyticsReportRequester().requestDriverVehicleAssociationReportByDriverToEmail(variables.startDate, variables.endDate, variables.destinationEmails, variables.wantedIds),
  });

  const publishDriverSafetyReportRequest = useMutation<string, unknown, DriverSafetyReportVariables>({
    mutationFn: (variables) =>
      getAnalyticsReportRequester().requestDriverSafetyReportToEmail(
        variables.startTimestamp,
        variables.endTimestamp,
        variables.destinationEmails,
        variables.reportFormat,
        variables.speedEventType,
        variables.driverIds
      ),
  });

  const publishDriverProductivityReportRequest = useMutation<string, unknown, ProductivityReportVariables>({
    mutationFn: (variables) =>
      getAnalyticsReportRequester().requestDriverProductivityToEmail(
        variables.startTimestamp,
        variables.endTimestamp,
        variables.wantedIds,
        variables.reportType,
        variables.destinationEmails
      ),
  });

  const publishVehicleProductivityReportRequest = useMutation<string, unknown, ProductivityReportVariables>({
    mutationFn: (variables) =>
      getAnalyticsReportRequester().requestVehicleProductivityToEmail(
        variables.startTimestamp,
        variables.endTimestamp,
        variables.wantedIds,
        variables.reportType,
        variables.destinationEmails
      ),
  });

  const publishTriggerEventReportRequest = useMutation<string, unknown, TriggerEventsReportVariables>({
    mutationFn: (variables) =>
      getAnalyticsReportRequester().requestTriggerEventsReportToEmail(
        variables.startTimestamp,
        variables.endTimestamp,
        variables.destinationEmails,
        variables.ruleId,
        variables.ruleName,
        variables.vehicleGroupIds,
        variables.vehicleIds,
        variables.geofenceIds,
        variables.conditionIds
      ),
    onError: () => {
      Alert.error('Erro ao exportar relatório. Tente novamente mais tarde.');
    },
  });

  const exportSpeedingPathReportRequest = useMutation<Blob, unknown, SpeedingPathReportVariables>({
    mutationFn: (variables) =>
      getAnalyticsReportRequester().requestSpeedingPathReport(variables.startTimestamp, variables.endTimestamp, variables.vehicleId, variables.reportFormat),
  });

  const exportDistanceDrivenReport = useMutation<Blob, unknown, { period: DateRange; wantedIds?: UUID[] }>({
    mutationFn: (variables) => getPerformanceReportsRequester().getDistanceDrivenReport(variables.period, variables.wantedIds),
  });

  const exportDriverOutOfWorkJourneyReport = useMutation<
    Blob,
    unknown,
    {
      period: DateRange;
      workStartInMillis: number;
      workEndInMillis: number;
      wantedIds?: UUID[];
    }
  >({
    mutationFn: (variables) =>
      getPerformanceReportsRequester().getDriversOutOfJourneyReport(variables.period, variables.workStartInMillis, variables.workEndInMillis, variables.wantedIds),
  });

  const exportVehicleOutOfWorkJourneyReport = useMutation<
    Blob,
    unknown,
    {
      period: DateRange;
      workStartInMillis: number;
      workEndInMillis: number;
      wantedIds?: UUID[];
    }
  >({
    mutationFn: (variables) =>
      getPerformanceReportsRequester().getVehiclesOutOfJourneyReport(variables.period, variables.workStartInMillis, variables.workEndInMillis, variables.wantedIds),
  });

  return {
    publishVehicleIdleEngineReportRequest,
    publishVehicleIdleEngineEventReportRequest,
    publishDriverIdleEngineReportRequest,
    publishDriverIdleEngineEventReportRequest,
    publishGeofenceEventsReportRequest,
    publishDriverVehicleAssociationReportByVehicleRequest,
    publishDriverVehicleAssociationReportByDriverRequest,
    publishDriverProductivityReportRequest,
    publishVehicleProductivityReportRequest,
    publishDriverSafetyReportRequest,
    publishTriggerEventReportRequest,
    exportSpeedingPathReportRequest,
    exportDistanceDrivenReport,
    exportDriverOutOfWorkJourneyReport,
    exportVehicleOutOfWorkJourneyReport,
  };
};

export default useAnalyticsReport;
